/**
 * 사람이 대화에 올린 파일.
 *
 * 이력서를 올려 인사등록을 채우는 것 같은 일에 쓴다. 본문은 base64 이고, 게이트웨이가
 * 그것을 모델 메시지의 파트로 바꿔 넣는다 (`ErpService/agent.go`).
 */
export type UploadedFile = { name: string; mime: string; data: string };

/**
 * 파일 전부를 합친 상한.
 *
 * ⚠ **게이트웨이와 같은 것을 센다 — base64 글자 수다.** 디코드한 바이트가 아니다.
 * 여기서 통과한 것이 서버에서 막히면 사람은 왜 막혔는지 알 길이 없다. 값이 갈리면
 * 안 되므로 `agentMaxFileBytes` 와 짝을 이룬다.
 */
export const MAX_UPLOAD_BYTES = 8 << 20;

/** 모델이 실제로 읽는 형식. 나머지는 받아 놓고 엉뚱한 답을 내느니 여기서 거절한다. */
export function uploadReadable(mime: string): boolean {
  return mime.startsWith('image/') || mime === 'application/pdf';
}

/**
 * 올린 파일에 문제가 있으면 **사람에게 그대로 보일 이유**를 돌려준다. 없으면 빈 문자열.
 *
 * 서버도 같은 검사를 하지만 여기서 먼저 본다 — 8MB 를 올려 놓고 왕복을 기다린 뒤에
 * 거절당하면 그 시간이 통째로 버려진다. 문구를 서버와 맞춰 둔다.
 */
export function uploadTrouble(files: UploadedFile[]): string {
  let total = 0;
  for (const one of files) {
    if (!one.name || !one.mime || !one.data) return '파일 이름·형식·내용이 모두 있어야 합니다.';
    if (!uploadReadable(one.mime)) {
      return `${one.mime} 형식은 아직 읽지 못합니다. 그림이나 PDF 로 올려 주세요.`;
    }
    total += one.data.length;
  }
  return total > MAX_UPLOAD_BYTES ? '올린 파일이 너무 큽니다. 나눠서 올려 주세요.' : '';
}

/**
 * 올린 파일을 계획 프롬프트에 알린다.
 *
 * **내용을 여기서 요약하지 않는다.** 파일 자체가 같은 메시지에 실려 가므로 모델이
 * 직접 읽는다. 이 줄이 하는 일은 하나다 — 거기 무엇이 왔는지 이름으로 알리고,
 * **읽은 것만 쓰라**고 못박는 것. 안 그러면 흐릿한 스캔에서 그럴듯한 값을 짓는다.
 */
export function describeUploads(files: UploadedFile[]): string {
  if (!files.length) return '';
  const names = files.map((one) => one.name).join(', ');
  return `\n\n사람이 이 대화에 파일을 올렸다: ${names}.`
    + ' 그 파일이 이 메시지에 함께 실려 있으니 직접 읽어라.'
    + ' **파일에서 실제로 읽은 값만 쓴다.** 흐리거나 안 보이는 칸은 비워 둬라 —'
    + ' 지어내면 사람이 그것을 찾아 고쳐야 하고, 틀린 줄도 모르고 저장될 수 있다.';
}

/**
 * 이미 붙인 파일은 다시 붙이지 않는다.
 *
 * 사람은 붙였는지 잊고 다시 고른다. 그대로 두면 같은 그림이 두 번 실려 토큰을 두 배로
 * 태우고, 문서에도 두 번 붙는다. 스펙 §4.6 이 적어 둔 규칙인데 **구현에 없었다** —
 * 요구·시험 표에 행조차 없어서 아무도 못 봤다(순방향 감사 2026-09-01).
 *
 * 이름만으로는 가르지 못한다. 사람이 다른 그림을 같은 이름으로 저장해 두는 일이 있다.
 */
export function dropDuplicates(
  already: UploadedFile[],
  incoming: UploadedFile[],
): UploadedFile[] {
  const key = (one: UploadedFile) => `${one.name}\u0000${one.mime}\u0000${one.data}`;
  const seen = new Set(already.map(key));
  const fresh: UploadedFile[] = [];
  for (const one of incoming) {
    if (seen.has(key(one))) continue;
    seen.add(key(one));
    fresh.push(one);
  }
  return fresh;
}
